import {
  ComponentPropsWithoutRef,
  LegacyRef,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import {
  fetchQuery,
  graphql,
  useFragment,
  useLazyLoadQuery,
  useRelayEnvironment,
} from "react-relay";
import Loader from "@/common/Loader";
import { logger } from "@/common/logger";
import { assertNever } from "@/utils/helpers";
import { cn } from "@/utils/tailwind";
import { DEFAULT_TOAST_DURATION, toast } from "@/utils/toast";
import {
  MarimoIframeHandle,
  MarimoIframeProps,
  MarimoIframeStatus,
} from "../MarimoIframe";
import { WorkspaceViewer } from "./WorkspaceViewer";
import {
  WorkspaceRenderer,
  WorkspaceRendererInteraction,
  WorkspaceRendererProps,
} from "./WorkspaceRenderer";
import WorkspacePhaseProgress from "../WorkspacePhaseProgress";
import { WorkspaceViewFragment$key } from "./__generated__/WorkspaceViewFragment.graphql";
import { WorkspaceViewRendererQuery } from "./__generated__/WorkspaceViewRendererQuery.graphql";
import {
  WorkspaceViewViewerQuery,
  WorkspaceViewViewerQuery$data,
} from "./__generated__/WorkspaceViewViewerQuery.graphql";
import { WorkspaceViewEditorWarmupQuery } from "./__generated__/WorkspaceViewEditorWarmupQuery.graphql";
import MarimoIframeStatusProgress from "../MarimoIframeStatusProgress";

/// How much time in milliseconds since the workspace is shown to automatically preload a viewer.
///
/// Short on purpose: starting the viewer is what claims a warm pod from the
/// runner pool, and a claim only beats a cold start while the pool still has
/// one. Long enough to let the renderer paint first.
const PRELOAD_VIEWER_MILLIS = 500;

/// How much time until we show a notification that the iframe is being loaded.
const LOADING_VIEWER_CONFIRM_DELAY = 800;

const Fragment = graphql`
  fragment WorkspaceViewFragment on Node {
    id
    ... on WorkspaceVersion {
      publishedAt
      viewerCanEdit: viewerCan(action: UPDATE_WORKSPACE)
    }
  }
`;

const RendererQuery = graphql`
  query WorkspaceViewRendererQuery($id: ID!) {
    node(id: $id) {
      ... on Workspace {
        renderer {
          ...WorkspaceRendererFragment
        }
      }
      ... on WorkspaceVersion {
        renderer {
          ...WorkspaceRendererFragment
        }
      }
    }
  }
`;

const ViewerQuery = graphql`
  query WorkspaceViewViewerQuery($id: ID!) {
    node(id: $id) {
      ... on Workspace {
        viewer {
          phase
          ...WorkspaceViewerFragment
          ...WorkspacePhaseProgressFragment
        }
      }
      ... on WorkspaceVersion {
        viewer {
          phase
          ...WorkspaceViewerFragment
          ...WorkspacePhaseProgressFragment
        }
      }
    }
  }
`;

const EditorWarmupQuery = graphql`
  query WorkspaceViewEditorWarmupQuery($id: ID!) {
    node(id: $id) {
      ... on WorkspaceVersion {
        editor @ifAllowed {
          id
          phase
        }
      }
    }
  }
`;

type WorkspaceViewType =
  | "renderer"
  | "renderer-background"
  | "pending-viewer"
  | "viewer";

export interface WorkspaceViewProps extends ComponentPropsWithoutRef<"div"> {
  workspace: WorkspaceViewFragment$key;
  hideCode?: boolean | undefined;
}

export function WorkspaceView({
  workspace: workspaceFragment,
  hideCode = true,
  ...props
}: WorkspaceViewProps) {
  const workspace = useFragment(Fragment, workspaceFragment);
  // Draft versions have no renderer (their render cache is only built on
  // publish), so they render directly via the live `viewer`. Published versions
  // (and the live workspace) start from the cached renderer and swap to the
  // viewer on interaction.
  const isDraft = workspace.publishedAt === null;
  const initialView: WorkspaceViewType = isDraft ? "viewer" : "renderer";
  // Same condition as the "Open Editor" action in the workspace header: only a
  // draft the viewer may edit is ever opened in the editor, so that is the only
  // place a pre-created edit runner would be used.
  const warmEditor = isDraft && workspace.viewerCanEdit === true;
  const [nextViewType, setViewType] = useState<WorkspaceViewType>(initialView);
  const viewType = useDeferredValue(nextViewType);
  const iframeRef = useRef<MarimoIframeHandle>(null);
  const interaction = useRef<WorkspaceRendererInteraction | null>(null);

  /// Reset view when workspace changes
  useEffect(() => {
    setViewType(initialView);
  }, [workspace.id, initialView]);

  /// Preload viewer
  useEffect(() => {
    if (viewType !== "renderer") return;

    const timeout = setTimeout(
      () => setViewType("renderer-background"),
      PRELOAD_VIEWER_MILLIS,
    );
    return () => clearTimeout(timeout);
  }, [workspace.id, viewType]);

  /// Forward renderer interaction to runner
  useEffect(() => {
    if (viewType !== "viewer") return;
    if (interaction.current === null) return;

    const action = interaction.current;
    interaction.current = null;

    switch (action.type) {
      case "click":
        iframeRef.current?.click(action.objectId, {
          value: action.value,
          testid: action.testid,
        });
        break;

      case "change":
        iframeRef.current?.change(action.objectId, {
          value: action.value,
        });
        break;

      default:
        assertNever(action);
    }
  }, [viewType]);

  /// Notify users when viewer is loading
  interface State {
    timeout: ReturnType<typeof setTimeout>;
    toastId: string;
    progress:
      | { from: "backend"; viewer: ViewerRef }
      | { from: "frontend"; status: MarimoIframeStatus };
  }
  const stateRef = useRef<Partial<State>>({});
  const onClearState = (dismiss = true) => {
    const { timeout, toastId } = stateRef.current;
    stateRef.current = {};

    clearTimeout(timeout);
    if (toastId && dismiss) {
      toast.dismiss(toastId);
    }
  };
  const onStartNotifying = () => {
    const toastId =
      stateRef.current.toastId ??
      (stateRef.current.toastId = crypto.randomUUID());

    const { progress } = stateRef.current;
    const done = progress?.from === "frontend" && progress.status === "ready";

    toast.create(
      <div className="flex flex-col gap-1">
        <p>
          <FormattedMessage defaultMessage="Interactive workspace is loading..." />
        </p>
        {(() => {
          switch (stateRef.current.progress?.from) {
            case "backend":
              return (
                <WorkspacePhaseProgress
                  workspace={stateRef.current.progress.viewer}
                  scale={0.5}
                />
              );
            case "frontend":
              return (
                <MarimoIframeStatusProgress
                  kind={done ? "success" : "info"}
                  status={stateRef.current.progress.status}
                  shift={50}
                  scale={0.5}
                />
              );
          }
        })()}
      </div>,
      {
        id: toastId,
        dismissible: done,
        duration: done ? DEFAULT_TOAST_DURATION : Infinity,
        level: done ? "success" : "info",
      },
    );
  };
  const onViewerMounted = useCallback((viewer: ViewerRef) => {
    onClearState();
    stateRef.current.progress = { from: "backend", viewer };
    stateRef.current.timeout = setTimeout(
      onStartNotifying,
      LOADING_VIEWER_CONFIRM_DELAY,
    );
  }, []);
  const onViewerStatusChanged = useCallback((status: MarimoIframeStatus) => {
    stateRef.current.progress = { from: "frontend", status };
    if (stateRef.current.toastId) {
      onStartNotifying();
    }
    if (status === "ready") {
      onClearState(false);
      setViewType("viewer");
    }
  }, []);
  useEffect(() => onClearState, []);

  /// A published version whose render cache is missing (a slot hydrated
  /// without it, a stale image) has no static render to show or to click on.
  /// The live viewer is loading underneath either way, so promote it instead
  /// of leaving the pane empty.
  const onRendererError = useCallback(() => {
    setViewType((view) =>
      view === "renderer" || view === "renderer-background"
        ? "pending-viewer"
        : view,
    );
  }, []);

  let view;
  switch (viewType) {
    case "renderer":
      view = [
        <WorkspaceViewRenderer
          key="renderer"
          {...props}
          workspaceId={workspace.id}
          hideCode={hideCode}
          isTransitionPending={nextViewType === "pending-viewer"}
          onInteracted={(action) => {
            interaction.current = action;
            setViewType("pending-viewer");
          }}
          onRenderError={onRendererError}
        />,
      ];
      break;

    case "renderer-background":
      view = [
        <WorkspaceViewRenderer
          key="renderer"
          {...props}
          workspaceId={workspace.id}
          hideCode={hideCode}
          isTransitionPending={nextViewType !== "renderer-background"}
          onInteracted={(action) => {
            interaction.current = action;
            setViewType(
              iframeRef.current?.isReady ? "viewer" : "pending-viewer",
            );
          }}
          onRenderError={onRendererError}
        />,
        <WorkspaceViewViewer
          key="runner"
          {...props}
          iframeRef={iframeRef}
          workspaceId={workspace.id}
          hideCode={hideCode}
          isTransitionPending
          warmEditor={warmEditor}
        />,
      ];
      break;

    case "pending-viewer":
      view = [
        <WorkspaceViewRenderer
          key="renderer"
          {...props}
          workspaceId={workspace.id}
          hideCode={hideCode}
          isTransitionPending
        />,
        <WorkspaceViewViewer
          key="runner"
          {...props}
          iframeRef={iframeRef}
          workspaceId={workspace.id}
          hideCode={hideCode}
          isTransitionPending
          warmEditor={warmEditor}
          onMounted={onViewerMounted}
          onStatusChanged={onViewerStatusChanged}
        />,
      ];
      break;

    case "viewer":
      view = [
        <WorkspaceViewViewer
          key="runner"
          {...props}
          iframeRef={iframeRef}
          workspaceId={workspace.id}
          hideCode={hideCode}
          isTransitionPending={false}
          warmEditor={warmEditor}
        />,
      ];
      break;

    default:
      assertNever(viewType);
  }

  return <Suspense fallback={<Loader {...props} />}>{view}</Suspense>;
}

interface SubviewProps extends ComponentPropsWithoutRef<"div"> {
  workspaceId: ID;
  hideCode: boolean;
  isTransitionPending: boolean;
}

function WorkspaceViewRenderer({
  workspaceId,
  hideCode,
  isTransitionPending,
  onInteracted,
  onRenderError,
  className,
  ...props
}: SubviewProps & {
  onInteracted?: WorkspaceRendererProps["onInteracted"];
  onRenderError?: WorkspaceRendererProps["onRenderError"];
}) {
  // A runner's `url` is derived from the version's overview notebook, a
  // dependency Relay can't track — so refetch on mount (store-and-network) to
  // pick up notebook changes made elsewhere (e.g. the Files tab) instead of
  // serving a stale cached url.
  const { node } = useLazyLoadQuery<WorkspaceViewRendererQuery>(
    RendererQuery,
    { id: workspaceId },
    { fetchPolicy: "store-and-network" },
  );

  if (!node.renderer) {
    return null;
  }
  return (
    <WorkspaceRenderer
      {...props}
      workspace={node.renderer}
      hideCode={hideCode}
      className={cn(
        className,
        isTransitionPending && "opacity-70 pointer-events-none",
      )}
      onInteracted={onInteracted}
      onRenderError={onRenderError}
    />
  );
}

type ViewerRef = NonNullable<WorkspaceViewViewerQuery$data["node"]["viewer"]>;

/// Create the edit runner before the user asks for it, so opening the editor
/// finds one already serving.
///
/// Resolving `editor` is what creates the runner, so this is a plain fetch with
/// no UI. It waits for the viewer to be READY rather than firing alongside it:
/// a workspace can claim one warm pod, and two runners claiming at once could
/// take pods on two different nodes, which would give the workspace two
/// diverging copies of its storage.
function useWarmEditor(workspaceId: ID, isViewerReady: boolean) {
  const relay = useRelayEnvironment();
  const warmedFor = useRef<ID | null>(null);

  useEffect(() => {
    if (!isViewerReady || warmedFor.current === workspaceId) return;
    warmedFor.current = workspaceId;

    // Deliberately no cleanup: the fetch exists purely for its server-side
    // side effect (creating the runner), so aborting it on unmount — or on
    // StrictMode's immediate dev-time cleanup, which cancelled the first run
    // while the ref then blocked the second — would defeat the hook.
    fetchQuery<WorkspaceViewEditorWarmupQuery>(relay, EditorWarmupQuery, {
      id: workspaceId,
    }).subscribe({
      error: (error: unknown) => {
        // Clear the guard so a later render can retry what never happened.
        warmedFor.current = null;
        logger.warn({ error }, "Failed to warm the workspace editor");
      },
    });
  }, [relay, workspaceId, isViewerReady]);
}

function WorkspaceViewViewer({
  workspaceId,
  hideCode,
  isTransitionPending,
  warmEditor,
  onMounted,
  ...props
}: SubviewProps & {
  iframeRef?: LegacyRef<MarimoIframeHandle> | undefined;
  warmEditor?: boolean | undefined;
  onMounted?: ((viewer: ViewerRef) => void) | undefined;
  onStatusChanged?: MarimoIframeProps["onStatusChanged"];
}) {
  const {
    node: { viewer },
  } = useLazyLoadQuery<WorkspaceViewViewerQuery>(
    ViewerQuery,
    { id: workspaceId },
    // See WorkspaceViewRenderer: refetch so notebook changes are reflected.
    { fetchPolicy: "store-and-network" },
  );

  useWarmEditor(workspaceId, !!warmEditor && viewer?.phase === "READY");

  useEffect(() => {
    if (viewer) {
      onMounted?.(viewer);
    }
  }, [viewer, onMounted]);

  if (!viewer) {
    return null;
  }

  return (
    <WorkspaceViewer
      {...props}
      workspace={viewer}
      hideCode={hideCode}
      hidden={isTransitionPending}
    />
  );
}
